"use client";

import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

type BeforeAfterStudy = {
  id: string;
  before: {
    thumbnail: string;
    score: number;
  };
  after: {
    thumbnail: string;
    score: number;
  };
  improvement: number; // Percentage improvement
};

export default function BeforeAfterStudiesSection() {
  const { t } = useTranslation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Sample before/after case studies data
  const beforeAfterStudies: BeforeAfterStudy[] = [
    {
      id: "ba-case1",
      before: {
        thumbnail: "/thumb/thumb1-before.jpg",
        score: 62,
      },
      after: {
        thumbnail: "/thumb/thumb1-after.jpg",
        score: 89,
      },
      improvement: 27,
    },
    {
      id: "ba-case2",
      before: {
        thumbnail: "/thumb/thumb2-before.jpg",
        score: 74,
      },
      after: {
        thumbnail: "/thumb/thumb2-after.jpg",
        score: 84,
      },
      improvement: 10,
    },
    {
      id: "ba-case3",
      before: {
        thumbnail: "/thumb/thumb3-before.jpg",
        score: 80,
      },
      after: {
        thumbnail: "/thumb/thumb3-after.jpg",
        score: 85,
      },
      improvement: 5,
    },
    {
      id: "ba-case4",
      before: {
        thumbnail: "/thumb/thumb4-before.jpg",
        score: 51,
      },
      after: {
        thumbnail: "/thumb/thumb4-after.jpg",
        score: 81,
      },
      improvement: 30,
    },
    {
      id: "ba-case5",
      before: {
        thumbnail: "/thumb/thumb5-before.jpg",
        score: 67,
      },
      after: {
        thumbnail: "/thumb/thumb5-after.jpg",
        score: 82,
      },
      improvement: 15,
    },
  ];

  // Auto-scroll functionality
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    let animationFrameId: number;
    let scrollPosition = 0;
    const scrollSpeed = 0.5; // pixels per frame
    const containerWidth = scrollContainer.scrollWidth;
    const viewportWidth = scrollContainer.clientWidth;

    const scroll = () => {
      if (!scrollContainer) return;

      scrollPosition += scrollSpeed;

      // Instead of abruptly resetting to 0, check if we've gone past the end
      if (scrollPosition >= containerWidth - viewportWidth) {
        // Reset position to beginning without visual jump
        scrollPosition = 0;
        scrollContainer.scrollLeft = scrollPosition;
      } else {
        // Normal scrolling
        scrollContainer.scrollLeft = scrollPosition;
      }

      animationFrameId = requestAnimationFrame(scroll);
    };

    animationFrameId = requestAnimationFrame(scroll);

    // Pause scrolling when hovering
    const handleMouseEnter = () => {
      cancelAnimationFrame(animationFrameId);
    };

    const handleMouseLeave = () => {
      animationFrameId = requestAnimationFrame(scroll);
    };

    scrollContainer.addEventListener("mouseenter", handleMouseEnter);
    scrollContainer.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      cancelAnimationFrame(animationFrameId);
      scrollContainer.removeEventListener("mouseenter", handleMouseEnter);
      scrollContainer.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, []);

  return (
    <section className="overflow-hidden py-12 text-gray-900">
      <div className="container mx-auto mb-8">
        <h2 className="mb-6 text-center text-2xl font-bold">
          {t("beforeAfterStudies.title", "Before & After Transformations")}
        </h2>
        <p className="mx-auto max-w-2xl text-center text-sm">
          {t(
            "beforeAfterStudies.description",
            "See how our thumbnail optimization tool transforms low-performing thumbnails into high-converting assets."
          )}
        </p>
      </div>

      <div ref={scrollContainerRef} className="flex gap-4 overflow-x-hidden pb-6">
        {/* Duplicate the case studies to create a seamless loop effect */}
        {[...beforeAfterStudies, ...beforeAfterStudies].map((study, index) => (
          <div
            key={`${study.id}-${index}`}
            className="w-[300px] flex-none overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"
          >
            <div className="border-b border-gray-200 bg-gray-50 p-3 text-center">
              <div className="text-sm font-medium">
                {t("beforeAfterStudies.improvementLabel", "Improvement")}:
                <span className="ml-1 font-bold text-green-600">+{study.improvement}%</span>
              </div>
            </div>

            {/* Before row */}
            <div className="border-b border-gray-200 p-3">
              <div className="flex items-center gap-3">
                <div className="relative h-[60px] w-[107px] overflow-hidden rounded border border-gray-200">
                  <img
                    src={study.before.thumbnail}
                    alt={t("beforeAfterStudies.beforeThumbnail", "Before thumbnail")}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="mb-2 text-xs font-medium text-gray-500">
                  {t("beforeAfterStudies.before", "Before")}
                </div>
                <div className="flex flex-1" />
                <div className="flex h-[60px] w-[60px] flex-col items-center justify-center rounded-full border-4 border-red-100 bg-red-50">
                  <div className="text-xs text-gray-500">
                    {t("beforeAfterStudies.score", "Score")}
                  </div>
                  <div className="text-lg font-bold text-red-500">{study.before.score}</div>
                </div>
              </div>
            </div>

            {/* After row */}
            <div className="p-3">
              <div className="flex items-center gap-3">
                <div className="relative h-[60px] w-[107px] overflow-hidden rounded border border-gray-200">
                  <img
                    src={study.after.thumbnail}
                    alt={t("beforeAfterStudies.afterThumbnail", "After thumbnail")}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="mb-2 text-xs font-medium text-gray-500">
                  {t("beforeAfterStudies.after", "After")}
                </div>
                <div className="flex flex-1" />
                <div className="flex h-[60px] w-[60px] flex-col items-center justify-center rounded-full border-4 border-green-100 bg-green-50">
                  <div className="text-xs text-gray-500">
                    {t("beforeAfterStudies.score", "Score")}
                  </div>
                  <div className="text-lg font-bold text-green-600">{study.after.score}</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
